import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SoftCard } from '@/components/ui/soft-card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast';
import { useChannelActions } from '@/hooks/useChannelActions';

import { TEMPLATE_KEYS, TEMPLATE_LABELS, defaultEmailTemplates } from './defaultEmailTemplates';

const CHANNEL_LABEL = {
	'product-reservations': 'Product Reservations',
	'local-checkout': 'Local Checkout'
};

/**
 * Resolves the displayed copy for a template — retailer overrides on the
 * channel record win; falls back to the default per template. Lifted as a
 * named export so the test can drive it without rendering the card.
 */
export const resolveTemplate = (channel, key, shop) => {
	const override = channel?.config?.templates?.[key];
	const fallback = defaultEmailTemplates[key](shop);
	return {
		title: override?.title ?? fallback.title,
		message: override?.message ?? fallback.message
	};
};

/**
 * Merges a single edited template into the existing templates map so the
 * other two stay untouched. Exported for table-testing — the update handler
 * shallow-merges `config`, so we MUST send the full templates map; sending
 * only the edited key would clobber the others.
 */
export const mergeTemplate = (existingTemplates = {}, key, next) => ({
	...existingTemplates,
	[key]: { title: next.title, message: next.message }
});

/**
 * Email-template editor for both channels. Three templates per channel
 * (`placed`, `confirmation`, `cancellation`), each with a title + message
 * the retailer can override. The twin-channel "Apply to {other} emails"
 * checkbox is rendered only when the twin is also enabled, defaulting on,
 * matching the retailers-dashboard behaviour.
 */
export function EmailTemplatesCard({ shop, channelType, channels }) {
	const { update } = useChannelActions(shop.id);
	const { toast } = useToast();
	const channel = channels.find((c) => c.type === channelType);
	const twinType = channelType === 'product-reservations' ? 'local-checkout' : 'product-reservations';
	const twinChannel = channels.find((c) => c.type === twinType);
	const twinEnabled = twinChannel?.status === 'enabled';

	const [activeKey, setActiveKey] = useState('placed');
	const [title, setTitle] = useState('');
	const [message, setMessage] = useState('');
	const [applyToTwin, setApplyToTwin] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		const current = resolveTemplate(channel, activeKey, shop);
		setTitle(current.title);
		setMessage(current.message);
	}, [channel, activeKey, shop]);

	const handleSave = async () => {
		setSaving(true);
		try {
			await update(channelType, {
				config: {
					templates: mergeTemplate(channel?.config?.templates, activeKey, { title, message })
				}
			});

			if (twinEnabled && applyToTwin) {
				await update(twinType, {
					config: {
						templates: mergeTemplate(twinChannel?.config?.templates, activeKey, { title, message })
					}
				});
			}

			toast({ title: 'Template saved', variant: 'success' });
		} catch (err) {
			toast({
				title: 'Could not save template',
				description: err?.message ?? 'Please try again.',
				variant: 'destructive'
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<SoftCard className="space-y-5 !p-5 md:!p-6">
			<div>
				<h3 className="text-sm font-bold text-foreground">Configure email templates</h3>
				<p className="mt-1 text-xs text-muted-foreground">
					Edit the default text used in order management emails to match your tone of voice and offer additional information, such
					as pick-up windows.
				</p>
			</div>

			<Tabs value={activeKey} onValueChange={setActiveKey}>
				<TabsList>
					{TEMPLATE_KEYS.map((key) => (
						<TabsTrigger key={key} value={key} data-testid={`template-${key}`}>
							{TEMPLATE_LABELS[key]}
						</TabsTrigger>
					))}
				</TabsList>
			</Tabs>

			<div className="space-y-2">
				<Label htmlFor="template-title" className="text-xs font-semibold text-foreground">
					Header
				</Label>
				<Input
					id="template-title"
					value={title}
					onChange={(event) => setTitle(event.target.value)}
					data-testid="template-title-input"
				/>
			</div>

			<div className="space-y-2">
				<Label htmlFor="template-message" className="text-xs font-semibold text-foreground">
					Email message
				</Label>
				<Textarea
					id="template-message"
					value={message}
					onChange={(event) => setMessage(event.target.value)}
					className="min-h-[220px]"
					data-testid="template-message-input"
				/>
			</div>

			<div className="flex flex-wrap items-center justify-between gap-3 pt-2">
				{twinEnabled ? (
					<Checkbox
						label={`Apply to ${CHANNEL_LABEL[twinType]} emails`}
						checked={applyToTwin}
						onChange={(event) => setApplyToTwin(event.target.checked)}
						disabled={saving}
						data-testid="template-copy-twin"
					/>
				) : (
					<span />
				)}
				<Button onClick={handleSave} disabled={saving} variant="action">
					{saving ? 'Saving…' : 'Save template'}
				</Button>
			</div>
		</SoftCard>
	);
}
